import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Check, X, Clock, Calendar, Settings, Ban, Power, Users, Edit2, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCateringTab = ({ authHeaders, showMsg, loading, setLoading }) => {
  const [view, setView] = useState('settings');
  const [settings, setSettings] = useState({ catering_enabled: true, advance_booking_days: 90 });
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [itemForm, setItemForm] = useState({ name: '', category: '', price_per_person: '', is_veg: true, description: '' });
  const [slotForm, setSlotForm] = useState({ start_time: '', end_time: '', max_bookings: 3, max_guests: 500 });
  const [blockForm, setBlockForm] = useState({ date: '', reason: '' });
  const [expandedEnq, setExpandedEnq] = useState({});
  const [editingSlot, setEditingSlot] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [setRes, catRes, itemRes, slotRes, blockRes, enqRes] = await Promise.all([
        fetch(`${API}/api/catering/admin/settings`, { headers: authHeaders() }),
        fetch(`${API}/api/catering/admin/categories`, { headers: authHeaders() }),
        fetch(`${API}/api/catering/admin/items`, { headers: authHeaders() }),
        fetch(`${API}/api/catering/admin/slots`, { headers: authHeaders() }),
        fetch(`${API}/api/catering/admin/blocked-dates`, { headers: authHeaders() }),
        fetch(`${API}/api/catering/admin/enquiries`, { headers: authHeaders() })
      ]);
      if (setRes.ok) setSettings(await setRes.json());
      if (catRes.ok) setCategories(await catRes.json());
      if (itemRes.ok) setItems(await itemRes.json());
      if (slotRes.ok) setSlots(await slotRes.json());
      if (blockRes.ok) setBlockedDates(await blockRes.json());
      if (enqRes.ok) setEnquiries(await enqRes.json());
    } catch {}
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- Settings ----
  const toggleCatering = async () => {
    setLoading(true);
    const newVal = settings.catering_enabled ? "false" : "true";
    await fetch(`${API}/api/catering/admin/settings`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ key: "catering_enabled", value: newVal })
    });
    setSettings(s => ({ ...s, catering_enabled: newVal === "true" }));
    showMsg(newVal === "true" ? 'Catering enabled' : 'Catering disabled');
    setLoading(false);
  };

  const updateAdvanceDays = async (days) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/settings`, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ key: "advance_booking_days", value: String(days) })
    });
    setSettings(s => ({ ...s, advance_booking_days: days }));
    showMsg('Advance booking days updated');
    setLoading(false);
  };

  // ---- Seed ----
  const seedData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/catering/admin/seed`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      fetchAll();
      showMsg(data.detail === 'Already seeded' ? 'Already seeded!' : 'Catering menu & slots seeded!');
    } catch { showMsg('Seed failed', 'error'); }
    setLoading(false);
  };

  // ---- Categories ----
  const addCategory = async () => {
    if (!newCat.trim()) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/catering/admin/categories`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ name: newCat.trim(), order: categories.length + 1 }) });
      setNewCat(''); fetchAll(); showMsg('Category added!');
    } catch { showMsg('Failed', 'error'); }
    setLoading(false);
  };

  // ---- Items ----
  const addItem = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(`${API}/api/catering/admin/items`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ ...itemForm, price_per_person: parseFloat(itemForm.price_per_person) }) });
      setItemForm({ name: '', category: '', price_per_person: '', is_veg: true, description: '' }); fetchAll(); showMsg('Item added!');
    } catch { showMsg('Failed', 'error'); }
    setLoading(false);
  };

  const deactivateItem = async (id) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/items/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll(); showMsg('Item deactivated'); setLoading(false);
  };

  // ---- Slots ----
  const addSlot = async (e) => {
    e.preventDefault();
    if (!slotForm.start_time || !slotForm.end_time) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/catering/admin/slots`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ...slotForm, max_bookings: parseInt(slotForm.max_bookings), max_guests: parseInt(slotForm.max_guests) })
      });
      setSlotForm({ start_time: '', end_time: '', max_bookings: 3, max_guests: 500 });
      fetchAll(); showMsg('Time slot added!');
    } catch { showMsg('Failed to add slot', 'error'); }
    setLoading(false);
  };

  const updateSlot = async (slotId, updates) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/slots/${slotId}`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify(updates)
    });
    setEditingSlot(null); fetchAll(); showMsg('Slot updated'); setLoading(false);
  };

  const deactivateSlot = async (id) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/slots/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll(); showMsg('Slot deactivated'); setLoading(false);
  };

  // ---- Blocked Dates ----
  const blockDate = async (e) => {
    e.preventDefault();
    if (!blockForm.date) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/catering/admin/blocked-dates`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(blockForm)
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail); }
      setBlockForm({ date: '', reason: '' }); fetchAll(); showMsg('Date blocked!');
    } catch (err) { showMsg(err.message || 'Failed', 'error'); }
    setLoading(false);
  };

  const unblockDate = async (id) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/blocked-dates/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll(); showMsg('Date unblocked'); setLoading(false);
  };

  // ---- Enquiry Status ----
  const updateEnqStatus = async (id, status) => {
    setLoading(true);
    await fetch(`${API}/api/catering/admin/enquiries/${id}/status`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status })
    });
    fetchAll(); showMsg(`Status updated to ${status}`); setLoading(false);
  };

  const statuses = ['Pending', 'Confirmed', 'Quotation Sent', 'Contacted', 'Completed', 'Rejected', 'Cancelled'];
  const statusColors = {
    Pending: 'bg-yellow-50 text-yellow-700', Confirmed: 'bg-green-50 text-green-700',
    'Quotation Sent': 'bg-purple-50 text-purple-700', Contacted: 'bg-blue-50 text-blue-700',
    Completed: 'bg-emerald-50 text-emerald-700', Rejected: 'bg-red-50 text-red-600',
    Cancelled: 'bg-gray-100 text-gray-500', New: 'bg-blue-50 text-blue-700'
  };

  const tabs = [
    { key: 'settings', label: 'Settings', icon: <Settings size={14} /> },
    { key: 'slots', label: 'Time Slots', icon: <Clock size={14} />, count: slots.filter(s => s.active !== false).length },
    { key: 'blocked', label: 'Blocked Dates', icon: <Ban size={14} />, count: blockedDates.length },
    { key: 'items', label: 'Menu Items', icon: <Plus size={14} />, count: items.filter(i => i.active !== false).length },
    { key: 'bookings', label: 'Bookings', icon: <Calendar size={14} />, count: enquiries.length },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            data-testid={`catering-tab-${t.key}`}
            className={`px-3 py-2 text-xs font-semibold rounded-lg cursor-pointer transition-colors flex items-center gap-1.5 ${
              view === t.key ? 'bg-[#5C7A3A] text-white' : 'bg-white text-[#7a6555] border border-[#E8DFD3] hover:border-[#5C7A3A]/40'
            }`}
          >
            {t.icon} {t.label} {t.count !== undefined && <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px]">{t.count}</span>}
          </button>
        ))}
      </div>

      {/* ========== SETTINGS VIEW ========== */}
      {view === 'settings' && (
        <div className="space-y-4">
          <Card className="border border-[#E8DFD3] bg-white shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-bold text-[#2C1810] text-base mb-4 flex items-center gap-2"><Power size={16} className="text-[#5C7A3A]" /> Catering Availability</h3>
              <div className="flex items-center justify-between p-4 bg-[#FDF8F0] rounded-xl border border-[#E8DFD3]">
                <div>
                  <p className="text-sm font-semibold text-[#2C1810]">Catering Service</p>
                  <p className="text-xs text-[#7a6555] mt-0.5">When OFF, customers see an "Unavailable" page instead of the booking form.</p>
                </div>
                <button
                  onClick={toggleCatering}
                  disabled={loading}
                  data-testid="toggle-catering-btn"
                  className={`relative w-14 h-7 rounded-full transition-colors cursor-pointer ${settings.catering_enabled ? 'bg-[#5C7A3A]' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.catering_enabled ? 'translate-x-7' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <div className={`mt-2 text-xs font-semibold px-3 py-1.5 rounded-lg inline-block ${settings.catering_enabled ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`} data-testid="catering-status-badge">
                {settings.catering_enabled ? 'LIVE — Accepting Bookings' : 'OFF — Bookings Paused'}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[#E8DFD3] bg-white shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-bold text-[#2C1810] text-sm mb-3">Advance Booking Window</h3>
              <p className="text-xs text-[#7a6555] mb-3">How many days ahead customers can book catering.</p>
              <div className="flex items-center gap-3">
                <Input
                  type="number" min="7" max="365"
                  value={settings.advance_booking_days}
                  onChange={e => setSettings(s => ({ ...s, advance_booking_days: parseInt(e.target.value) || 90 }))}
                  className="w-24 text-sm"
                  data-testid="advance-days-input"
                />
                <span className="text-xs text-[#7a6555]">days</span>
                <Button onClick={() => updateAdvanceDays(settings.advance_booking_days)} disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-xs cursor-pointer" data-testid="save-advance-days">Save</Button>
              </div>
            </CardContent>
          </Card>

          {/* Seed */}
          <Card className="border border-[#E8DFD3] bg-white shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-bold text-[#2C1810] text-sm mb-2">Seed Default Data</h3>
              <p className="text-xs text-[#7a6555] mb-3">Load default categories, menu items, and time slots. Only works if no items exist.</p>
              <Button onClick={seedData} disabled={loading} className="bg-[#C4844A] hover:bg-[#b37440] text-white text-xs cursor-pointer" data-testid="seed-catering-btn">
                {loading ? <><Loader2 size={12} className="animate-spin mr-1" /> Seeding...</> : 'Seed Default Menu & Slots'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== SLOTS VIEW ========== */}
      {view === 'slots' && (
        <div className="space-y-4">
          <Card className="border border-[#E8DFD3] bg-white shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] text-sm mb-3 flex items-center gap-2"><Clock size={14} className="text-[#5C7A3A]" /> Add Time Slot</h3>
              <form onSubmit={addSlot} className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Start Time</label>
                  <Input type="time" value={slotForm.start_time} onChange={e => setSlotForm(f => ({ ...f, start_time: e.target.value }))} required className="text-xs" data-testid="slot-start" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">End Time</label>
                  <Input type="time" value={slotForm.end_time} onChange={e => setSlotForm(f => ({ ...f, end_time: e.target.value }))} required className="text-xs" data-testid="slot-end" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Max Bookings</label>
                  <Input type="number" min="1" value={slotForm.max_bookings} onChange={e => setSlotForm(f => ({ ...f, max_bookings: e.target.value }))} className="text-xs" data-testid="slot-max-bookings" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Max Guests</label>
                  <Input type="number" min="1" value={slotForm.max_guests} onChange={e => setSlotForm(f => ({ ...f, max_guests: e.target.value }))} className="text-xs" data-testid="slot-max-guests" />
                </div>
                <Button type="submit" disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-xs cursor-pointer" data-testid="add-slot-btn">
                  <Plus size={12} className="mr-1" /> Add Slot
                </Button>
              </form>
            </CardContent>
          </Card>

          {slots.length === 0 ? (
            <div className="text-center py-8 text-[#7a6555] text-sm">No time slots configured. Add one above or seed defaults from Settings.</div>
          ) : (
            <div className="space-y-2">
              {slots.map(slot => (
                <Card key={slot.id} className={`border shadow-sm ${slot.active === false ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-[#E8DFD3] bg-white'}`} data-testid={`admin-slot-${slot.id}`}>
                  <CardContent className="p-4">
                    {editingSlot === slot.id ? (
                      <SlotEditRow slot={slot} onSave={(updates) => updateSlot(slot.id, updates)} onCancel={() => setEditingSlot(null)} loading={loading} />
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 flex-1">
                          <Clock size={14} className="text-[#5C7A3A]" />
                          <span className="text-sm font-bold text-[#2C1810]">{slot.start_time} - {slot.end_time}</span>
                          {slot.active === false && <span className="text-[10px] bg-red-50 text-red-500 px-2 py-0.5 rounded-full">Inactive</span>}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#7a6555]">
                          <span className="flex items-center gap-1"><Calendar size={12} /> {slot.max_bookings} bookings</span>
                          <span className="flex items-center gap-1"><Users size={12} /> {slot.max_guests} guests</span>
                        </div>
                        <div className="flex gap-1">
                          <Button onClick={() => setEditingSlot(slot.id)} variant="ghost" size="sm" className="text-[#7a6555] hover:bg-[#FDF8F0] cursor-pointer" data-testid={`edit-slot-${slot.id}`}><Edit2 size={13} /></Button>
                          {slot.active !== false && (
                            <Button onClick={() => deactivateSlot(slot.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-50 cursor-pointer" data-testid={`delete-slot-${slot.id}`}><Trash2 size={13} /></Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== BLOCKED DATES VIEW ========== */}
      {view === 'blocked' && (
        <div className="space-y-4">
          <Card className="border border-[#E8DFD3] bg-white shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] text-sm mb-3 flex items-center gap-2"><Ban size={14} className="text-red-500" /> Block a Date</h3>
              <form onSubmit={blockDate} className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Date</label>
                  <Input type="date" value={blockForm.date} onChange={e => setBlockForm(f => ({ ...f, date: e.target.value }))} required className="text-xs w-44" data-testid="block-date-input" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Reason (optional)</label>
                  <Input value={blockForm.reason} onChange={e => setBlockForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g., Public Holiday" className="text-xs" data-testid="block-reason-input" />
                </div>
                <Button type="submit" disabled={loading} className="bg-red-500 hover:bg-red-600 text-white text-xs cursor-pointer" data-testid="block-date-btn">
                  <Ban size={12} className="mr-1" /> Block Date
                </Button>
              </form>
            </CardContent>
          </Card>

          {blockedDates.length === 0 ? (
            <div className="text-center py-8 text-[#7a6555] text-sm">No dates blocked. Blocked dates prevent customers from booking.</div>
          ) : (
            <div className="space-y-2">
              {blockedDates.map(bd => (
                <Card key={bd.id} className="border border-[#E8DFD3] bg-white shadow-sm" data-testid={`blocked-${bd.id}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Ban size={14} className="text-red-400" />
                      <span className="text-sm font-semibold text-[#2C1810]">{bd.date}</span>
                      {bd.reason && <span className="text-xs text-[#7a6555] bg-[#FDF8F0] px-2 py-0.5 rounded">{bd.reason}</span>}
                    </div>
                    <Button onClick={() => unblockDate(bd.id)} variant="ghost" size="sm" className="text-green-600 hover:bg-green-50 text-xs cursor-pointer" data-testid={`unblock-${bd.id}`}>
                      <Check size={12} className="mr-1" /> Unblock
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== MENU ITEMS VIEW ========== */}
      {view === 'items' && (
        <>
          {items.length === 0 && (
            <div className="text-center py-8">
              <p className="text-[#7a6555] mb-4">No catering menu items yet.</p>
              <Button onClick={seedData} disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer" data-testid="seed-catering-items-btn">{loading ? 'Seeding...' : 'Seed Default Menu'}</Button>
            </div>
          )}
          {items.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-1 border border-[#E8DFD3] h-fit bg-white shadow-sm">
                <CardContent className="p-5 space-y-4">
                  <h3 className="font-bold text-[#2C1810] text-sm flex items-center gap-2"><Plus size={14} className="text-[#5C7A3A]" /> Add Category</h3>
                  <div className="flex gap-2">
                    <Input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="Category name" className="text-xs" data-testid="new-catering-cat" />
                    <Button onClick={addCategory} disabled={!newCat.trim()} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer flex-shrink-0" data-testid="add-catering-cat"><Plus size={14} /></Button>
                  </div>
                  <h3 className="font-bold text-[#2C1810] text-sm flex items-center gap-2 pt-3 border-t border-[#E8DFD3]"><Plus size={14} className="text-[#5C7A3A]" /> Add Menu Item</h3>
                  <form onSubmit={addItem} className="space-y-2">
                    <Input value={itemForm.name} onChange={e => setItemForm(f => ({ ...f, name: e.target.value }))} placeholder="Item name" required className="text-xs" data-testid="catering-item-name" />
                    <Input list="catering-cats" value={itemForm.category} onChange={e => setItemForm(f => ({ ...f, category: e.target.value }))} placeholder="Category" required className="text-xs" data-testid="catering-item-cat" />
                    <datalist id="catering-cats">{categories.map(c => <option key={c.id} value={c.name} />)}</datalist>
                    <Input type="number" value={itemForm.price_per_person} onChange={e => setItemForm(f => ({ ...f, price_per_person: e.target.value }))} placeholder="Price per person" required className="text-xs" data-testid="catering-item-price" />
                    <Input value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="text-xs" data-testid="catering-item-desc" />
                    <label className="flex items-center gap-2 text-xs text-[#2C1810] cursor-pointer">
                      <input type="checkbox" checked={itemForm.is_veg} onChange={e => setItemForm(f => ({ ...f, is_veg: e.target.checked }))} className="rounded" data-testid="catering-item-veg" /> Vegetarian
                    </label>
                    <Button type="submit" disabled={loading} className="w-full bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer text-xs" data-testid="add-catering-item">{loading ? 'Adding...' : 'Add Item'}</Button>
                  </form>
                </CardContent>
              </Card>
              <div className="lg:col-span-2 space-y-2">
                {items.filter(i => i.active !== false).map(item => (
                  <Card key={item.id} className="border border-[#E8DFD3] bg-white shadow-sm" data-testid={`catering-admin-item-${item.id}`}>
                    <CardContent className="p-3 flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${item.is_veg ? 'bg-green-500' : 'bg-red-500'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#2C1810] truncate">{item.name}</p>
                        <p className="text-xs text-[#7a6555]">{item.category}</p>
                      </div>
                      <span className="text-sm font-bold text-[#5C7A3A]">{'\u20B9'}{item.price_per_person}</span>
                      <Button onClick={() => deactivateItem(item.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-50 cursor-pointer"><Trash2 size={14} /></Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ========== BOOKINGS VIEW ========== */}
      {view === 'bookings' && (
        <div className="space-y-3">
          {enquiries.length === 0 ? (
            <div className="text-center py-12 text-[#7a6555]">No catering bookings yet.</div>
          ) : enquiries.map(enq => (
            <Card key={enq.id} className="border border-[#E8DFD3] bg-white shadow-sm" data-testid={`enquiry-${enq.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-[#2C1810] text-sm">{enq.customer_name}</p>
                      {enq.booking_ref && <span className="text-[10px] font-mono bg-[#FDF8F0] text-[#5C7A3A] px-2 py-0.5 rounded" data-testid={`ref-${enq.id}`}>{enq.booking_ref}</span>}
                    </div>
                    <p className="text-xs text-[#7a6555]">{enq.mobile} {enq.email ? `· ${enq.email}` : ''}</p>
                  </div>
                  <select
                    value={enq.status}
                    onChange={e => updateEnqStatus(enq.id, e.target.value)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border-0 cursor-pointer ${statusColors[enq.status] || 'bg-gray-50 text-gray-600'}`}
                    data-testid={`status-select-${enq.id}`}
                  >
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs mb-2">
                  <div><span className="text-[#7a6555]">Event:</span> <span className="font-medium text-[#2C1810]">{enq.event_type}</span></div>
                  <div><span className="text-[#7a6555]">Date:</span> <span className="font-medium text-[#2C1810]">{enq.event_date}</span></div>
                  <div><span className="text-[#7a6555]">Time:</span> <span className="font-medium text-[#2C1810]">{enq.time_slot_label || '—'}</span></div>
                  <div><span className="text-[#7a6555]">Guests:</span> <span className="font-medium text-[#2C1810]">{enq.guests}</span></div>
                  <div><span className="text-[#7a6555]">Total:</span> <span className="font-bold text-[#5C7A3A]">{'\u20B9'}{enq.estimated_total?.toLocaleString('en-IN')}</span></div>
                </div>
                <button onClick={() => setExpandedEnq(prev => ({ ...prev, [enq.id]: !prev[enq.id] }))} className="text-xs text-[#5C7A3A] font-medium cursor-pointer hover:underline">
                  {expandedEnq[enq.id] ? 'Hide details' : `View ${enq.items?.length || 0} items & details`}
                </button>
                {expandedEnq[enq.id] && (
                  <div className="mt-3 pt-3 border-t border-[#E8DFD3] space-y-1">
                    <p className="text-xs text-[#7a6555]">Location: {enq.event_location}</p>
                    {enq.notes && <p className="text-xs text-[#7a6555]">Notes: {enq.notes}</p>}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(enq.items || []).map((it, i) => (
                        <span key={i} className="text-[10px] bg-[#FDF8F0] px-2 py-1 rounded text-[#2C1810]">{it.name} ({'\u20B9'}{it.price_per_person}/person)</span>
                      ))}
                    </div>
                    <p className="text-xs text-[#7a6555] mt-1">Cost/guest: {'\u20B9'}{enq.cost_per_guest} · Created: {enq.created_at ? new Date(enq.created_at).toLocaleString() : '—'}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Inline edit row for slots
const SlotEditRow = ({ slot, onSave, onCancel, loading }) => {
  const [form, setForm] = useState({
    start_time: slot.start_time,
    end_time: slot.end_time,
    max_bookings: slot.max_bookings,
    max_guests: slot.max_guests
  });

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
      <div>
        <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Start</label>
        <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} className="text-xs" data-testid="edit-slot-start" />
      </div>
      <div>
        <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">End</label>
        <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className="text-xs" data-testid="edit-slot-end" />
      </div>
      <div>
        <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Max Book</label>
        <Input type="number" min="1" value={form.max_bookings} onChange={e => setForm(f => ({ ...f, max_bookings: parseInt(e.target.value) || 1 }))} className="text-xs" data-testid="edit-slot-max-bookings" />
      </div>
      <div>
        <label className="text-[10px] font-medium text-[#7a6555] mb-1 block">Max Guests</label>
        <Input type="number" min="1" value={form.max_guests} onChange={e => setForm(f => ({ ...f, max_guests: parseInt(e.target.value) || 1 }))} className="text-xs" data-testid="edit-slot-max-guests" />
      </div>
      <div className="flex gap-1">
        <Button onClick={() => onSave(form)} disabled={loading} className="bg-[#5C7A3A] hover:bg-[#4a6530] text-white text-xs cursor-pointer flex-1" data-testid="save-slot-btn"><Check size={12} /></Button>
        <Button onClick={onCancel} variant="ghost" className="text-[#7a6555] text-xs cursor-pointer" data-testid="cancel-slot-btn"><X size={12} /></Button>
      </div>
    </div>
  );
};

export default AdminCateringTab;
